import { css, SerializedStyles } from '@emotion/react';
import * as React from 'react';
import { useHoneycombTheme } from '../../theming';

export type PopoverPanelClasses = 'floating' | 'inner';

// Static styles
export const POPOVER_STATIC_STYLES: {
    [key in PopoverPanelClasses]: SerializedStyles;
} = {
    floating: css`
        z-index: var(--hc-z-index-popup);
        outline: none;
    `,

    inner: css`
        transition-property: opacity, transform;
        transition-timing-function: var(--hc-transition-easing-ease-in-out);

        /* ---------------------------------------------------------------------
         * Placement transform origins
         * Used by scale-based and grow-based animations
         * ------------------------------------------------------------------ */

        /* TOP placements: grow from bottom */
        &[data-placement='top'] {
            transform-origin: bottom;
        }
        &[data-placement='top-start'] {
            transform-origin: bottom left;
            &:dir(rtl) {
                transform-origin: bottom right;
            }
        }
        &[data-placement='top-end'] {
            transform-origin: bottom right;
            &:dir(rtl) {
                transform-origin: bottom left;
            }
        }

        /* BOTTOM placements: grow from top */
        &[data-placement='bottom'] {
            transform-origin: top;
        }
        &[data-placement='bottom-start'] {
            transform-origin: top left;
            &:dir(rtl) {
                transform-origin: top right;
            }
        }
        &[data-placement='bottom-end'] {
            transform-origin: top right;
            &:dir(rtl) {
                transform-origin: top left;
            }
        }

        /* LEFT placements: grow from right */
        &[data-placement='left'] {
            transform-origin: right;
            &:dir(rtl) {
                transform-origin: left;
            }
        }
        &[data-placement='left-start'] {
            transform-origin: top right;
            &:dir(rtl) {
                transform-origin: top left;
            }
        }
        &[data-placement='left-end'] {
            transform-origin: bottom right;
            &:dir(rtl) {
                transform-origin: bottom left;
            }
        }

        /* RIGHT placements: grow from left */
        &[data-placement='right'] {
            transform-origin: left;
            &:dir(rtl) {
                transform-origin: right;
            }
        }
        &[data-placement='right-start'] {
            transform-origin: top left;
            &:dir(rtl) {
                transform-origin: top right;
            }
        }
        &[data-placement='right-end'] {
            transform-origin: bottom left;
            &:dir(rtl) {
                transform-origin: bottom right;
            }
        }

        /* ---------------------------------------------------------------------
         * Animation: scale + fade (default)
         * Uniform scale on both axes
         * ------------------------------------------------------------------ */

        &[data-animation='scale-fade'] {
            &[data-status='initial'],
            &[data-status='close'] {
                opacity: 0;
                transform: scale(0.8);
            }

            &[data-status='open'] {
                opacity: 1;
                transform: scale(1);
                transition-duration: var(--duration-open);
            }

            &[data-status='close'] {
                transition-duration: var(--duration-close);
            }
        }

        /* ---------------------------------------------------------------------
         * Animation: grow + fade
         * Axis-aware scale (preserves width or height depending on placement)
         * ------------------------------------------------------------------ */

        &[data-animation='grow-fade'] {
            &[data-status='initial'],
            &[data-status='close'] {
                opacity: 0;
            }

            &[data-status='open'] {
                opacity: 1;
                transition-duration: var(--duration-open);
            }

            &[data-status='close'] {
                transition-duration: var(--duration-close);
            }
        }

        /* Vertical growth (top / bottom placements) */
        &[data-animation='grow-fade'][data-placement^='top'],
        &[data-animation='grow-fade'][data-placement^='bottom'] {
            &[data-status='initial'],
            &[data-status='close'] {
                transform: scaleY(0);
            }

            &[data-status='open'] {
                transform: scaleY(1);
            }
        }

        /* Horizontal growth (left / right placements) */
        &[data-animation='grow-fade'][data-placement^='left'],
        &[data-animation='grow-fade'][data-placement^='right'] {
            &[data-status='initial'],
            &[data-status='close'] {
                transform: scaleX(0);
            }

            &[data-status='open'] {
                transform: scaleX(1);
            }
        }

        /* ---------------------------------------------------------------------
         * Animation: slide + fade (legacy / optional)
         * ------------------------------------------------------------------ */

        &[data-animation='slide-fade'] {
            &[data-status='initial'],
            &[data-status='close'] {
                opacity: 0;
                transform: translate3d(var(--popover-slide-x, 0), var(--popover-slide-y, 0), 0);
            }

            &[data-status='open'] {
                opacity: 1;
                transform: translate3d(0, 0, 0);
                transition-duration: var(--duration-open);
            }

            &[data-status='close'] {
                transition-duration: var(--duration-close);
            }
        }

        &[data-animation='slide-fade'][data-placement^='top'] {
            --popover-slide-y: 8px;
        }
        &[data-animation='slide-fade'][data-placement^='bottom'] {
            --popover-slide-y: -8px;
        }
        &[data-animation='slide-fade'][data-placement^='left'] {
            --popover-slide-x: 8px;
        }
        &[data-animation='slide-fade'][data-placement^='right'] {
            --popover-slide-x: -8px;
        }

        /* ---------------------------------------------------------------------
         * Animation: none
         * ------------------------------------------------------------------ */

        &[data-animation='none'] {
            transition: none;
            opacity: 1;
            transform: none;
        }
    `,
};

export function usePopoverAnimationCssVariables() {
    const { theme } = useHoneycombTheme();
    const { animationDuration } = theme.components.themes.Popover;

    return React.useMemo(
        () =>
            ({
                '--duration-open': `${animationDuration.open}ms`,
                '--duration-close': `${animationDuration.close}ms`,
            } as React.CSSProperties),
        [animationDuration.open, animationDuration.close]
    );
}
